import { storage } from '../utils/storage.js';
import { getCurrentLanguage, interpolate } from '../translations.js';
import fetchCache from '../utils/fetchCache.js';
import { createCustomVoiceSelect } from '../components/customVoiceSelect.js';
import {
  createCreditMultiplierBadge,
  getEffectiveVoiceMultiplier,
} from '../utils/creditMultiplier.js';
import { getVoiceLanguageCodesForProvider } from '../utils/languageData.js';

const voicesCache = {};

export const ELEVENLABS_PROVIDER_VOICE_ID = /^[A-Za-z0-9]{20}$/;

export const isElevenLabsProviderVoiceId = (voiceId) => (
  ELEVENLABS_PROVIDER_VOICE_ID.test(String(voiceId || '').trim())
);

/**
 * @param {string | null | undefined} provider
 * @returns {string}
 */
export const formatVoiceProviderLabel = (provider) => {
  if (!provider) return 'elevenlabs';
  return String(provider).toLowerCase();
};

/**
 * @param {{ provider?: string | null, name?: string }} voice
 * @returns {string}
 */
export const formatVoiceDisplayName = (voice) => {
  const provider = formatVoiceProviderLabel(voice?.provider);
  const name = voice?.name || 'Voice';
  return `${provider} - ${name}`;
};

/**
 * Pick a random voice whose effective credit multiplier is at most maxMultiplier.
 * Falls back to the first catalog entry when no cheap voices exist.
 * @param {Array<{ provider?: string | null, multiplier?: number | string | null }>} voices
 * @param {number} [maxMultiplier=0.5]
 * @returns {object | null}
 */
export const pickCheapDefaultVoice = (voices, maxMultiplier = 0.5) => {
  const list = Array.isArray(voices) ? voices : [];
  const pool = list.filter((v) => getEffectiveVoiceMultiplier(v) <= maxMultiplier);
  if (!pool.length) return list[0] || null;
  return pool[Math.floor(Math.random() * pool.length)];
};

/**
 * Random catalog ElevenLabs voice at the provider base rate.
 * Skips voices whose own multiplier pushes the effective rate above 1.
 * @param {Array<{ provider?: string | null, multiplier?: number | string | null }>} voices
 * @returns {object | null}
 */
export const pickDefaultElevenLabsVoice = (voices) => {
  const list = Array.isArray(voices) ? voices : [];
  const elevenLabs = list.filter((voice) => String(voice?.provider || 'elevenlabs').toLowerCase() === 'elevenlabs');
  const baseRate = elevenLabs.filter((voice) => getEffectiveVoiceMultiplier(voice) <= 1);
  const pool = baseRate.length ? baseRate : elevenLabs;
  if (!pool.length) return null;
  return pool[Math.floor(Math.random() * pool.length)];
};

/**
 * Load voices from the CDN.
 * @param {string} [subDir='voices'] - Folder under /data/ that contains voices.json (e.g., 'voices', 'male_voices').
 */
export async function loadVoices(subDir = 'voices') {
    if (voicesCache[subDir]) return voicesCache[subDir];

    const baseUrl = (typeof window !== 'undefined' && window.CONFIG && window.CONFIG.CDN_URL)
        ? window.CONFIG.CDN_URL
        : '/kssdafront';

    const data = await fetchCache.fetchJSON(`${baseUrl}/data/${subDir}/voices.json`);
    const voices = (data.voices || [])
      .map((voice) => ({
        ...voice,
        provider: voice.provider || 'elevenlabs',
        isActive: voice.isActive !== false,
      }))
      .filter((voice) => voice.isActive);
    voicesCache[subDir] = voices;
    return voicesCache[subDir];
}

export async function getVoiceById(id, subDir = 'voices') {
    const voices = await loadVoices(subDir);
    return voices.find(v => v.id === id) || null;
}

export async function getAllVoices(subDir = 'voices') {
    return loadVoices(subDir);
}

export async function getVoicesByLanguage(language, subDir = 'voices') {
    const voices = await loadVoices(subDir);
    return voices.filter(v => v.languages.includes(language));
}

const FLAGS_SPRITE_URL = '/assets/images/flags/flags-sprite.svg';

let flagsSpriteLoadPromise = null;

/** Ensures the flags sprite is injected into the DOM so fragment refs (#flag-xx) resolve. */
export async function ensureFlagsSpriteLoaded() {
    if (document.querySelector('#flags-sprite')) return;
    if (flagsSpriteLoadPromise) return flagsSpriteLoadPromise;
    flagsSpriteLoadPromise = (async () => {
        try {
            const response = await fetch(FLAGS_SPRITE_URL);
            if (!response.ok) return;
            const svgText = await response.text();
            const container = document.createElement('div');
            container.id = 'flags-sprite';
            container.style.display = 'none';
            container.innerHTML = svgText;
            document.body.insertBefore(container, document.body.firstChild);
        } catch (err) {
            console.error('[voiceService] Failed to load flags sprite:', err);
        }
    })();
    return flagsSpriteLoadPromise;
}

export async function populateVoiceSelect(selectElement, defaultText = 'Select a Voice', language = null, subDir = 'voices') {
    await ensureFlagsSpriteLoaded();
    selectElement.classList.add('replacing');

    try {
        const voices = language ? await getVoicesByLanguage(language, subDir) : await getAllVoices(subDir);
        const dropdown = buildVoiceSelect(selectElement, defaultText, voices);
        selectElement.parentNode.replaceChild(dropdown, selectElement);
        return dropdown;
    } catch (error) {
        selectElement.classList.remove('replacing');
        throw error;
    }
}

function buildVoiceSelect(originalSelect, defaultText, voices) {
    const voiceOptions = voices.map((voice) => {
        const displayName = formatVoiceDisplayName(voice);
        const effectiveMultiplier = getEffectiveVoiceMultiplier(voice);
        return {
            value: voice.id,
            label: displayName,
            dataset: {
              voiceName: voice.name,
              voiceProvider: formatVoiceProviderLabel(voice.provider),
            },
            renderOption: () => {
                const row = document.createElement('div');
                row.className = 'voice-option-content';

                const nameSpan = document.createElement('span');
                nameSpan.className = 'voice-name';
                nameSpan.textContent = displayName;
                row.appendChild(nameSpan);

                const badge = createCreditMultiplierBadge(effectiveMultiplier, {
                  hideWhenOne: true,
                  variant: 'voice',
                });
                if (badge) row.appendChild(badge);

                return row;
            },
            renderSelected: (optionEl) => {
                const frag = document.createDocumentFragment();

                const nameSpan = document.createElement('span');
                nameSpan.className = 'voice-name';
                nameSpan.textContent = displayName;
                frag.appendChild(nameSpan);

                const badge = optionEl.querySelector('.credit-multiplier')?.cloneNode(true);
                if (badge) frag.appendChild(badge);

                return frag;
            },
        };
    });

    const voiceUseIdTemplate = () => {
        const lang = getCurrentLanguage();
        return window.translations?.[lang]?.hangout?.character_modal?.voice_use_id
            ?? window.translations?.en?.hangout?.character_modal?.voice_use_id
            ?? 'Use voice ID {id}';
    };
    const voiceSearchPlaceholder = () => {
        const lang = getCurrentLanguage();
        return window.translations?.[lang]?.hangout?.character_modal?.voice_search_placeholder
            ?? 'Search voices or paste a voice ID';
    };

    const container = createCustomVoiceSelect({
        id: originalSelect.id,
        className: 'custom-voice-select character-voice-type-control',
        placeholder: defaultText,
        closeScopeSelector: '.voice-controls-group',
        searchable: true,
        searchPlaceholder: voiceSearchPlaceholder(),
        getSearchOptions: (query, baseOptions) => {
            const trimmed = query.trim();
            const needle = trimmed.toLowerCase();
            const catalog = baseOptions.filter((opt) => opt.value && opt.dataset?.customVoice !== 'true');
            const matched = !needle
                ? catalog
                : catalog.filter((opt) => `${opt.dataset?.voiceName || ''} ${opt.label || ''}`.toLowerCase().includes(needle));
            const placeholder = !needle ? baseOptions.find((opt) => opt.value === '') : null;
            const rows = placeholder ? [placeholder, ...matched] : matched;
            if (isElevenLabsProviderVoiceId(trimmed) && !catalog.some((opt) => opt.value === trimmed)) {
                rows.unshift({
                    value: trimmed,
                    label: interpolate(voiceUseIdTemplate(), { id: trimmed }),
                    dataset: {
                        voiceName: trimmed,
                        voiceProvider: 'elevenlabs',
                        customVoice: 'true',
                    },
                });
            }
            return rows;
        },
        commitSearchQuery: (query, baseOptions) => {
            const trimmed = query.trim();
            if (!isElevenLabsProviderVoiceId(trimmed)) return null;
            const catalog = baseOptions.find((opt) => opt.value === trimmed && opt.dataset?.customVoice !== 'true');
            if (catalog) return catalog;
            return {
                value: trimmed,
                label: interpolate(voiceUseIdTemplate(), { id: trimmed }),
                dataset: {
                    voiceName: trimmed,
                    voiceProvider: 'elevenlabs',
                    customVoice: 'true',
                },
            };
        },
        options: [
            { value: '', label: defaultText },
            ...voiceOptions,
        ],
        onBeforeOpen: () => {
            document.querySelectorAll('.character-voice-dropdown-content').forEach((el) => {
                el.style.display = 'none';
            });
        },
        onSelect: (value, optionEl) => {
            const voiceName = optionEl.dataset.voiceName
                || optionEl.querySelector('.voice-name')?.textContent
                || '';
            const voiceProvider = optionEl.dataset.voiceProvider || 'elevenlabs';

            container.dataset.voiceName = voiceName;
            container.dataset.voiceProvider = voiceProvider;

            if (value && optionEl.dataset.customVoice !== 'true') {
                const provider = voiceProvider;
                const supportedCodes = getVoiceLanguageCodesForProvider(provider);
                const currentLanguage = storage.get('voiceLanguage');
                const voiceLanguage = supportedCodes.includes(currentLanguage)
                    ? currentLanguage
                    : (supportedCodes.includes('en') ? 'en' : (supportedCodes[0] || 'en'));
                storage.set('voiceLanguage', voiceLanguage);
            }

            container.dispatchEvent(new CustomEvent('change', {
                detail: {
                  value,
                  voiceName,
                  voiceId: value,
                  voiceProvider,
                  customVoice: optionEl.dataset.customVoice === 'true',
                },
            }));
        },
    });

    container.getVoiceName = () => container.dataset.voiceName || '';
    container.getVoiceProvider = () => container.dataset.voiceProvider || 'elevenlabs';

    return container;
}
